var _class;
import React, { Component, useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Box, Button, Flex, Heading, Icon, Popover, Text, Tooltip } from 'core/components';
import { TbChevronsRight } from 'react-icons/tb';
import { Field, formConsumer, SegmentedControl, Select, Switch } from 'core/form';
import $dataviews, { dataviewCategoryTitles } from 'app/stores/$dataviews';
import $metrics from 'app/stores/metrics/$metrics';
import { usePanelEditContext } from 'app/views/boards/PanelEditContext';
import { useFormContext } from 'core/form/FormContext';
import Section from 'app/views/boards/SidebarSection';
import { getEnumHealthyValue, isEnumItem } from '@kentik/ui-shared/query/enumUtils';
import UdeThresholdEditor from './UdeThresholdEditor';
import YAxisMetricSection from './YAxisMetricSection';

/**
 * Returns true when the metric details contain a non-empty Values map.
 * Used as an extra guard so the fallback path in isTimelineEligibleMetric
 * also requires real schema values before enabling the timeline view.
 */
function hasNonEmptyValuesMap(metricDetails) {
  if (!metricDetails || typeof metricDetails !== 'object') {
    return false;
  }
  const values = metricDetails.Values;
  return !!values && typeof values === 'object' && Object.keys(values).length > 0;
}

/**
 * Returns true if the metric should be eligible for the timeline view.
 * Requires a non-empty Values map from the measurement schema in all paths:
 * - either the metric is detected as an enum metric (has Values map), or
 * - the metric name matches a known status-like fallback AND the schema has
 *   returned Values (i.e. the backend has caught up).
 * The HealthyValue fallback map is used only for coloring in the renderer,
 * not for eligibility.
 */
function isTimelineEligibleMetric(metricName, metricDetails) {
  return isEnumItem(metricDetails) || getEnumHealthyValue(undefined, metricName) !== undefined && hasNonEmptyValuesMap(metricDetails);
}
const ChartTypeKey = 'config.chart.type';

/** Display-only label: drop a trailing " Chart" suffix so the grid reads more compactly. */
function toDisplayLabel(label) {
  return label.replace(/\s*Chart$/i, '').trim();
}
/**
 * Returns true if the currently-selected metrics make Measurement-Specific
 * (timeline) views eligible. Mirrors the per-view disabled logic.
 */
function useMeasurementSpecificEligible(form) {
  return useMemo(() => {
    const metrics = form?.getValue('config.data.metrics');
    const measurement = form?.getValue('config.data.measurement');
    if (!metrics?.length || !measurement) {
      return false;
    }
    const measurementModel = $metrics?.measurementModel?.(measurement);
    return metrics.some(m => isTimelineEligibleMetric(m.name, measurementModel?.get?.(`storage.Metrics.${m.name}`)));
  }, [form]);
}
function FormChartTypeTileImpl({
  view,
  panel,
  onSelected
}) {
  const form = useFormContext();
  const onClick = useCallback(() => {
    if (!form) {
      return;
    }
    form.setValue(ChartTypeKey, view.value);

    // Timeline view requires "last" aggregation to preserve discrete state
    // transitions; force the metric's window.fn and rollup accordingly.
    // Use the first enum metric found (there may be non-enum metrics co-selected).
    if (view.value === 'timelineView') {
      const metrics = form?.getValue('config.data.metrics') || [];
      const measurement = form?.getValue('config.data.measurement');
      const measurementModel = measurement ? $metrics.measurementModel?.(measurement) : undefined;
      const enumMetric = metrics.find(m => isTimelineEligibleMetric(m.name, measurementModel?.get?.(`storage.Metrics.${m.name}`)));
      const metricName = enumMetric?.name;
      if (metricName) {
        const currentWindowFn = form?.getValue('config.data.window.fn') || {};
        form?.setValue('config.data.window.fn', {
          ...currentWindowFn,
          [metricName]: 'last'
        });
        const currentRollups = form?.getValue('config.data.rollups') || [];
        const hasMetricRollup = currentRollups.some(r => r.metric === metricName);
        const updatedRollups = hasMetricRollup ? currentRollups.map(r => r.metric === metricName ? {
          ...r,
          fn: 'last'
        } : r) : [...currentRollups, {
          metric: metricName,
          fn: 'last'
        }];
        form?.setValue('config.data.rollups', updatedRollups);
      }
    }

    /** Sync the form's config to the panel model (triggering history tracking) and kick off autoSave */
    const formValues = form.getValues();
    panel.safeSet({
      config: {
        ...panel.get('config'),
        ...formValues.config
      }
    });
    if (form.onChange) {
      form.onChange({
        form,
        formValues
      });
    }
    onSelected?.();
  }, [form, panel, view.value, onSelected]);
  const active = form?.getValue(ChartTypeKey) === view.value;
  let disabled = false;
  if (view.category === 'Measurement-Specific') {
    disabled = true;
    const metrics = form?.getValue('config.data.metrics');
    const measurement = form?.getValue('config.data.measurement');
    if (metrics?.length >= 1 && measurement) {
      const measurementModel = $metrics?.measurementModel?.(measurement);
      const hasEnumMetric = metrics.some(m => isTimelineEligibleMetric(m.name, measurementModel?.get?.(`storage.Metrics.${m.name}`)));
      if (hasEnumMetric) {
        disabled = false;
      }
    }
    if (!active && disabled) {
      return null;
    }
  }
  const tile = /*#__PURE__*/React.createElement(Button, {
    minimal: true,
    active: active,
    disabled: disabled && !active,
    onClick: onClick,
    width: "100%",
    style: {
      whiteSpace: 'normal'
    }
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Icon, {
    icon: view.icon,
    size: 16
  }), /*#__PURE__*/React.createElement(Text, {
    textAlign: "left",
    fontSize: 12,
    width: "100%"
  }, toDisplayLabel(view.label))));
  if (view.category === 'Measurement-Specific' && disabled) {
    return /*#__PURE__*/React.createElement(Tooltip, {
      content: "Timeline view is only available for status-style metrics."
    }, tile);
  }
  return tile;
}
const FormChartTypeTile = observer(FormChartTypeTileImpl);
const VizLimitSelect = observer(({
  panel
}) => {
  const form = useFormContext();
  const chartType = form?.getValue(ChartTypeKey);
  const panelEditContext = usePanelEditContext();
  const handleVizLimitChange = useCallback(() => {
    if (!form || !panel.queryModel) {
      return;
    }
    const vizLimit = form.getValue('config.data.viz.limit');
    panel.queryModel.vizLimit = vizLimit;
    panelEditContext?.onRunQuery();
  }, [form, panel, panelEditContext]);
  const hiddenTypes = ['table', 'matrix', 'gauge', 'geoHeatMap', 'sankey', 'timelineView', 'horizon'];
  if (hiddenTypes.includes(chartType)) {
    return null;
  }
  return /*#__PURE__*/React.createElement(Flex, {
    alignItems: "center",
    gap: "4px",
    px: "12px",
    pt: 2
  }, /*#__PURE__*/React.createElement(Text, {
    as: "span",
    fontSize: "12px"
  }, "Show"), /*#__PURE__*/React.createElement(Field, {
    name: "config.data.viz.limit",
    showLabel: false,
    m: 0,
    onChange: handleVizLimitChange
  }, /*#__PURE__*/React.createElement(Select, {
    menuWidth: 80
  })), /*#__PURE__*/React.createElement(Text, {
    as: "span",
    fontSize: "12px"
  }, "Series"));
});
const UdeChartTypeSelectorImpl = ({
  panel
}) => {
  const form = useFormContext();
  const [isOpen, setIsOpen] = useState(false);
  const measurementSpecificEligible = useMeasurementSpecificEligible(form);
  const currentType = form?.getValue(ChartTypeKey);
  const selectedView = $dataviews.viewTypeOptions.find(view => view.value === currentType);
  const content = /*#__PURE__*/React.createElement(Box, {
    p: 1,
    width: 325
  }, /*#__PURE__*/React.createElement(Flex, {
    flexDirection: "column",
    gap: "4px"
  }, dataviewCategoryTitles.flatMap(category => {
    const views = $dataviews.viewTypeOptions.filter(view => view.category === category && !view.hideFromViewMenu && (
    // Hide Measurement-Specific views unless eligible (or currently selected)
    category !== 'Measurement-Specific' || measurementSpecificEligible || view.value === currentType));
    return views.map(view => /*#__PURE__*/React.createElement(FormChartTypeTile, {
      key: view.value,
      view: view,
      panel: panel,
      onSelected: () => setIsOpen(false)
    }));
  })));
  return /*#__PURE__*/React.createElement(Section, {
    title: "Type",
    mb: 0,
    isCollapsible: false,
    defaultIsOpen: true
  }, /*#__PURE__*/React.createElement(Box, {
    px: "12px"
  }, /*#__PURE__*/React.createElement(Popover, {
    isOpen: isOpen,
    onInteraction: nextOpen => setIsOpen(nextOpen),
    content: content,
    placement: "bottom-start",
    fill: true
  }, /*#__PURE__*/React.createElement(Button, {
    small: true,
    fill: true,
    textAlign: "left",
    onClick: () => setIsOpen(open => !open),
    rightIcon: "caret-down",
    icon: selectedView?.icon && /*#__PURE__*/React.createElement(Icon, {
      icon: selectedView.icon,
      size: 14
    })
  }, selectedView ? toDisplayLabel(selectedView.label) : 'Select chart type'))), /*#__PURE__*/React.createElement(VizLimitSelect, {
    panel: panel
  }));
};
const UdeChartTypeSelector = observer(UdeChartTypeSelectorImpl);

// @ts-expect-error -- inject typed store
let UdeChartSettingsForm = formConsumer(_class = observer(_class = class UdeChartSettingsForm extends Component {
  render() {
    const {
      form,
      panel,
      onToggleCollapse
    } = this.props;
    const chartType = form.getValue(ChartTypeKey);
    return /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      width: "100%",
      height: "100%",
      bg: "cardBackground",
      borderRadius: "6px 0 6px 6px",
      border: "thin",
      style: {
        scrollbarWidth: 'thin'
      },
      overflowY: "auto"
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      pl: 2,
      pr: 0,
      py: 1,
      flexShrink: 0,
      borderBottom: "thin",
      bg: "cardBackground",
      style: {
        position: 'sticky',
        top: 0,
        zIndex: 1
      }
    }, /*#__PURE__*/React.createElement(Heading, {
      level: 6,
      m: 0,
      fontSize: 14,
      fontWeight: "bold",
      flex: 1
    }, "Chart"), onToggleCollapse && /*#__PURE__*/React.createElement(Tooltip, {
      content: "Close Chart options"
    }, /*#__PURE__*/React.createElement(Button, {
      minimal: true,
      icon: TbChevronsRight,
      onClick: onToggleCollapse,
      "aria-label": "Close Chart options"
    }))), /*#__PURE__*/React.createElement(UdeChartTypeSelector, {
      panel: panel
    }), chartType !== 'table' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(YAxisMetricSection, null), /*#__PURE__*/React.createElement(Section, {
      title: "Table",
      actions: /*#__PURE__*/React.createElement(Flex, {
        alignItems: "center",
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement(Field, {
        name: "config.chart.legend.enable",
        showLabel: false,
        m: 0
      }, /*#__PURE__*/React.createElement(Switch, {
        showSwitchLabel: false,
        "aria-label": "Enable table"
      })))
    }, /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      px: 2,
      gap: 1,
      pt: 1
    }, /*#__PURE__*/React.createElement(Field, {
      name: "config.chart.legend.position",
      m: 0
    }, /*#__PURE__*/React.createElement(SegmentedControl, {
      small: true,
      fill: true
    })), /*#__PURE__*/React.createElement(Field, {
      name: "config.chart.legend.mode",
      m: 0
    }, /*#__PURE__*/React.createElement(SegmentedControl, {
      small: true,
      fill: true
    }))))), /*#__PURE__*/React.createElement(Section, {
      title: "Thresholds",
      actions: /*#__PURE__*/React.createElement(Flex, {
        alignItems: "center",
        onClick: e => e.stopPropagation()
      }, /*#__PURE__*/React.createElement(Field, {
        name: "config.chart.thresholds.enabled",
        showLabel: false,
        m: 0
      }, /*#__PURE__*/React.createElement(Switch, {
        showSwitchLabel: false,
        "aria-label": "Enable thresholds"
      })))
    }, /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      px: 2,
      gap: 1,
      pt: 1
    }, /*#__PURE__*/React.createElement(UdeThresholdEditor, null))));
  }
}) || _class) || _class;
export default UdeChartSettingsForm;